function groupedFixed(value: number, decimalPlaces: number): string {
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
    useGrouping: true
  }).format(value);
}

/** Format a finite value to three significant figures for chart tooltips. */
export function formatTooltipNumber(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  if (value === 0 || Object.is(value, -0)) return '0';

  const magnitude = Math.abs(value);
  if (magnitude >= 1e9 || magnitude < 1e-6) {
    return value.toExponential(2).replace('e+', 'e');
  }

  const rounded = Number(value.toPrecision(3));
  const exponent = Math.floor(Math.log10(Math.abs(rounded)));
  return groupedFixed(rounded, Math.max(0, 2 - exponent));
}

function appendUnit(value: string, unit: string): string {
  if (!unit) return value;
  return `${value}${unit === '%' ? '' : ' '}${unit}`;
}

export function formatTooltipValue(value: unknown, unit = ''): string {
  const formatted = formatTooltipNumber(value);
  return formatted ? appendUnit(formatted, unit) : 'No value';
}

function compactDurationNumber(value: number): string {
  return formatTooltipNumber(value)
    .replace(/(\.\d*?[1-9])0+$/, '$1')
    .replace(/\.0+$/, '');
}

/** Keep short captures precise and render long recorder durations readably. */
export function formatEventDuration(milliseconds: unknown): string {
  if (typeof milliseconds !== 'number' || !Number.isFinite(milliseconds) || milliseconds < 0) {
    return 'Unknown duration';
  }
  const roundedMilliseconds = Number(milliseconds.toPrecision(3));
  if (roundedMilliseconds < 1000) return `${compactDurationNumber(milliseconds)} ms`;

  const seconds = milliseconds / 1000;
  if (seconds < 60) return `${compactDurationNumber(seconds)} s`;
  if (seconds < 60 * 60) {
    const roundedSeconds = Math.round(seconds);
    const minutes = Math.floor(roundedSeconds / 60);
    const remainder = roundedSeconds % 60;
    return remainder ? `${minutes} min ${remainder} s` : `${minutes} min`;
  }
  if (seconds < 24 * 60 * 60) {
    const roundedMinutes = Math.round(seconds / 60);
    const hours = Math.floor(roundedMinutes / 60);
    const remainder = roundedMinutes % 60;
    return remainder ? `${hours} h ${remainder} min` : `${hours} h`;
  }

  const roundedHours = Math.round(seconds / (60 * 60));
  const days = Math.floor(roundedHours / 24);
  const remainder = roundedHours % 24;
  const dayLabel = `${compactDurationNumber(days)} d`;
  return remainder ? `${dayLabel} ${remainder} h` : dayLabel;
}
